import { brighten, deepen } from "utils/inks";

// The ONE frontend vocabulary module (apps/web/CLAUDE.md): display labels/colors
// for every closed vocabulary, PLUS the static scoring-config mirror — hand-synced
// with apps/api/ciq_config.py v0.16.0 (ADR 0002; no meta endpoint by decision).
// Runtime numbers (Score DNA weights, weight_mass) always come from each series
// point's `components` payload — this mirror only feeds labels and config chips.
// The imputation constants (ADR 0012) are deliberately NOT mirrored here: every
// imputed component carries its own value and its `imputed` / source="imputed"
// marker in the payload, so the UI never needs to know the config numbers.

// 0.17.0 (2026-09-02) added the `jewelry` industry to the vocabulary, and
// catches up two earlier additions that landed WITHOUT a version bump —
// `automotive` (2026-09-01) and `beauty` (2026-09-02). No weight, decay,
// strategy or imputation number changed in any of them, so every mirror below
// stands. INDUSTRY_LABELS had also missed `beauty`, which is not cosmetic: the
// Leaderboard and Industry Stats screens both treat an industry absent from
// that map as GENERAL, so a whole cohort loses its own filter.
export const CONFIG_VERSION = "0.17.0";

// EVERY identity colour below — item types, industries, brands — is AUTHORED
// for the dark ground the app shipped on and DERIVED for the light one it
// has had since 2026-09-11 (user call: "some tags are not visible", "colors
// used are sometimes too light for this design"): `ink()` keeps the hue and
// saturation and lowers the lightness until the colour reads at 4.5:1 on
// white (utils/inks.js) — the text bar, because these inks are printed as
// TEXT (a type chip, a brand's name on Compare, a gap badge), not only as
// fills. The authored values stay in the `*_AUTHORED` tables so the hue
// relationships the comments below argue over are still what is compared;
// what the app renders is the derived table. `inkFor` is the same step for
// a colour that arrives as DATA (a Lab block payload's series colour).
export const ink = (hex) => deepen(hex, undefined, 4.5);
export const inkFor = (hex) => ink(hex);
// …and the SAME identity on the DUSK (2026-09-28, the compare screen on the
// brand page's violet gradient): the paper ink is too dark to read there,
// so every table also carries a `<key>Dusk` twin — the authored colour
// LIFTED (hue and saturation kept) until it reads at 4.5:1 on the dusk's
// MID tone — the gradient's violet stop at 72% (`--ground-dusk`,
// index.css), the lightest ground a chart card sits on, so a line that
// clears it clears the darker top too. A screen on the gradient prints
// the twin on the ground and in its glass cards, and the paper ink inside
// its off-white boxes, where the paper tokens are re-established.
const DUSK_INK_GROUND = "#482180";
export const duskInk = (hex) => brighten(hex, DUSK_INK_GROUND, 4.5);
const inkTable = (table, key) =>
  Object.fromEntries(
    Object.entries(table).map(([k, v]) => [
      k,
      { ...v, [key]: ink(v[key]), [`${key}Dusk`]: duskInk(v[key]) },
    ]),
  );

// Item-type identity: stable color + display label (design guide §2).
const TYPE_META_AUTHORED = {
  content: { c: "#4FC3F7", n: "Content" },
  event: { c: "#FF5A76", n: "Events" },
  collab: { c: "#B388FF", n: "Collabs" },
  merch: { c: "#FFB74D", n: "Merch" },
  archive_reissue: { c: "#D7A97C", n: "Archive re-issue" },
  experience: { c: "#4ADE80", n: "Experiences" },
  capsule: { c: "#FDE047", n: "Capsules" },
  styling: { c: "#94A3B8", n: "Styling" },
  brand_codes: { c: "#60A5FA", n: "Brand codes" },
  art_direction: { c: "#F472B6", n: "Art direction" },
  community: { c: "#2DD4BF", n: "Community" },
};
export const TYPE_META = inkTable(TYPE_META_AUTHORED, "c");
const FALLBACK_INK = ink("#8b7bff");
export const tmeta = (t) => TYPE_META[t] || { c: FALLBACK_INK, n: t };

// Static mirror of CIQ_CONFIG.itemTypes (kind / weight / halfLifeDays / graceDays).
// graceDays is the peak-plateau CEILING (ADR 0010): an item holds full strength for
// graceDays × base_score/100 days before decay starts — hence "≤" wherever it shows.
export const ITEM_TYPE_CONFIG = {
  content: { kind: "episodic", weight: 2.0, halfLifeDays: 60, graceDays: 14 },
  event: { kind: "episodic", weight: 1.5, halfLifeDays: 30, graceDays: 7 },
  collab: { kind: "episodic", weight: 2.0, halfLifeDays: 45, graceDays: 10 },
  merch: { kind: "episodic", weight: 1.0, halfLifeDays: 45, graceDays: 5 },
  archive_reissue: {
    kind: "episodic",
    weight: 1.2,
    halfLifeDays: 60,
    graceDays: 10,
  },
  experience: { kind: "episodic", weight: 1.0, halfLifeDays: 40, graceDays: 7 },
  capsule: { kind: "episodic", weight: 1.2, halfLifeDays: 30, graceDays: 5 },
  styling: { kind: "persistent", weight: 1.0 },
  brand_codes: { kind: "persistent", weight: 1.0 },
  art_direction: { kind: "persistent", weight: 1.0 },
  community: { kind: "persistent", weight: 1.2 },
};

export const SIG_META = {
  "financial:revenue_growth": { n: "Revenue growth", i: "📈" },
  "financial:stock_price_delta": { n: "Stock momentum", i: "💹" },
  "social:mentions": { n: "Social mentions", i: "💬" },
  "social:sentiment": { n: "Social sentiment", i: "❤️" },
  "search:search_volume": { n: "Search volume", i: "🔍" },
};
export const SIGNAL_ORDER = [
  "financial:revenue_growth",
  "financial:stock_price_delta",
  "social:mentions",
  "social:sentiment",
  "search:search_volume",
];
// Static mirror of CIQ_CONFIG.metricSignalWeights.
export const SIGNAL_WEIGHTS = {
  "financial:revenue_growth": 0.3,
  "financial:stock_price_delta": 0.2,
  "social:mentions": 0.2,
  "social:sentiment": 0.2,
  "search:search_volume": 0.2,
};

export const STRATEGY_LABELS = {
  scarcity: "Scarcity",
  opacity: "Opacity",
  aesthetic_innovation: "Aesthetic innovation",
  fandom: "Fandom",
  identity_signal: "Identity signal",
};
// Static mirror of CIQ_CONFIG.strategies[*].multiplier (cap 1.5 per play).
// Tiers (2026-07-27): fandom + identity_signal > aesthetic_innovation >
// scarcity > opacity. nostalgia + controversy_as_attention retired 2026-08-05
// (ADR 0014, config 0.15.0).
export const STRATEGY_MULTIPLIERS = {
  fandom: 1.35,
  identity_signal: 1.35,
  aesthetic_innovation: 1.28,
  scarcity: 1.22,
  opacity: 1.15,
};
export const STRATEGY_MULTIPLIER_CAP = 1.5;

// LEGACY (ADR 0013): the additive baseline term was removed in config 0.13.0.
// These labels survive only for pre-0.13 snapshot points, whose components
// still carry a `baselines` family that Score DNA must keep rendering.
export const BASELINE_LABELS = {
  geographic_presence: "Geographic presence",
  heritage_depth: "Heritage depth",
  distribution_scale: "Distribution scale",
};

// An industry MISSING from this map is silently collapsed into GENERAL by the
// Leaderboard and Industry Stats screens (both gate on `INDUSTRY_LABELS[code]`),
// so every code in CIQ_CONFIG.industries must appear here.
export const INDUSTRY_LABELS = {
  fashion: "Fashion",
  automotive: "Automotive",
  beauty: "Beauty",
  food: "Food & Bev",
  jewelry: "Jewelry",
  tech: "Tech",
};
export const industryLabel = (code) => INDUSTRY_LABELS[code] || code;

// Per-industry identity: the series/chip color on the Industry Stats screen.
// Same law as BRAND_STYLE lines — identity says WHO, never direction, so these
// must stay clear of green #00c805 / red #ff5449 (change), accent #8b7bff
// (signals) and gold #ffd60a (rank). Placeholder values pending design sign-off.
// `hue` is the same colour as a bare hue (degrees), for surfaces the app tints
// through `hsl(var(--hue) …)` — the demo home's industry chips and caption.
const INDUSTRY_STYLE_AUTHORED = {
  fashion: { line: "#e8b4d8", hue: 318 },
  automotive: { line: "#7fb4e0", hue: 207 },
  food: { line: "#62c99e", hue: 155 },
  // amber: the one hue family left that is clear of fashion's pink, automotive's
  // blue and food's jade — and of gold #ffd60a by being noticeably duller
  tech: { line: "#e0a458", hue: 33 },
  // orchid: more purple than fashion's pink #e8b4d8, and kept lighter and less
  // saturated than the signals accent #8b7bff so it never reads as a metric
  beauty: { line: "#c49ad6", hue: 282 },
  // cyan-teal: cooler and bluer than food's jade #62c99e, greener and lighter
  // than automotive's #7fb4e0 — the gap the other four leave open
  jewelry: { line: "#5ec5c9", hue: 182 },
};
export const INDUSTRY_STYLE = inkTable(INDUSTRY_STYLE_AUTHORED, "line");
const INDUSTRY_FALLBACK = { line: ink("#8ea0b5"), hue: 210 };
export const istyle = (code) => INDUSTRY_STYLE[code] || INDUSTRY_FALLBACK;

// Per-brand identity: `mono` (the monogram initials for a brand with no mark
// in assets/logos) + `line`, the series/bar color on the compare screen.
// Identity colors say WHO, never direction — they must stay clear of green
// #00c805 / red #ff5449 (change), accent #8b7bff (signals) and gold #ffd60a
// (rank/inertia). Placeholder values pending design sign-off.
// As of 2026-09-08 `bg` and `fg` are UNUSED: every brand tile in the app went
// BLACK AND WHITE (user call), so components/LogoTile paints one ink on one
// surface from the stylesheet and reads only `mono` here. The pairs are kept
// as the brands' identity record — and as what a tile would wear if colour
// ever comes back — but nothing renders them; `line` is still live.
const BRAND_STYLE_AUTHORED = {
  // BV's signature parakeet green collides with direction-green — neutral
  // burgundy identity instead (placeholder pending design sign-off)
  "bottega-veneta": {
    bg: "#5c2634",
    fg: "#f2e8e4",
    mono: "BV",
    line: "#e08fa9",
  },
  chanel: { bg: "#ece7dc", fg: "#141414", mono: "C", line: "#ece7dc" },
  dior: { bg: "#c5cbd6", fg: "#141414", mono: "D", line: "#c5cbd6" },
  "giorgio-armani": {
    bg: "#1c1c1e",
    fg: "#cbb9a2",
    mono: "GA",
    line: "#b3a48e",
  },
  gucci: { bg: "#1f4a3d", fg: "#e6ddc4", mono: "G", line: "#4fd8c4" },
  hermes: { bg: "#f37021", fg: "#fff", mono: "H", line: "#ff9500" },
  "louis-vuitton": {
    bg: "#40342a",
    fg: "#e2c992",
    mono: "LV",
    line: "#c9a05e",
  },
  prada: { bg: "#2e3f5c", fg: "#fff", mono: "PR", line: "#5ac8fa" },
  "saint-laurent": { bg: "#000", fg: "#f2ede3", mono: "SL", line: "#95aede" },
  // Starbucks house green tile (white-on-green like the siren mark); jade
  // line keeps clear of direction-green, the gucci precedent
  starbucks: { bg: "#006241", fg: "#fff", mono: "SB", line: "#62c99e" },
  // Valentino rosso collides with direction-red — deep maroon tile, orchid
  // line (placeholder pending design sign-off)
  valentino: { bg: "#7a1f2b", fg: "#f5e9dc", mono: "V", line: "#cf8be0" },
  // Red Bull red — the official hex (user call 2026-09-06: real brand colours
  // on the tiles now that the real marks are in), white bulls on it; the
  // line stays blue because red is direction-red on the charts
  "red-bull": { bg: "#db0a40", fg: "#fff", mono: "RB", line: "#5b8def" },
  // Coca-Cola's official red under the white script (user call 2026-09-06:
  // real brand colours on the tiles); pale salmon line keeps the series
  // clear of direction-red
  "coca-cola": { bg: "#f40009", fg: "#fff", mono: "CC", line: "#ffb4a2" },
  // McDonald's is red + gold, both reserved — the arches on the brand's dark
  // charcoal (its modern packaging look), burnt-orange line (placeholder
  // pending sign-off)
  mcdonalds: { bg: "#27251f", fg: "#ffbc0d", mono: "M", line: "#e07b39" },
  // 7-Eleven stripe green tile (white monogram), orange stripe as the line —
  // the teal tile keeps clear of direction-green, the starbucks precedent
  "7-eleven": { bg: "#008061", fg: "#fff", mono: "7", line: "#ff7a1a" },
  // Diageo (2026-09-12): the group's own mark is a plain red wordmark, which
  // is direction-red, so the tile takes the deep aged-whisky amber of its
  // portfolio instead and the line a copper — warmer and darker than
  // tech's amber industry line #e0a458 and mcdonalds' burnt-orange #e07b39,
  // duller than rank-gold (placeholder pending sign-off)
  diageo: { bg: "#5a2d0c", fg: "#f3dcb0", mono: "DG", line: "#c47a3a" },
  // Nike is black-and-white by identity — the swoosh's own palette. Volt
  // (#d7f205) is deliberately NOT used: it sits between rank-gold and
  // direction-green (placeholder pending sign-off)
  nike: { bg: "#111111", fg: "#fff", mono: "NK", line: "#f2f2f2" },
  // Zara's white-box minimalism — the inverse of nike's tile; cool grey line
  // (warm taupe would collide with giorgio-armani) (placeholder pending sign-off)
  zara: { bg: "#ffffff", fg: "#111111", mono: "Z", line: "#b0b7bd" },
  // Gap's official box navy (user call 2026-09-06: real brand colours), the
  // white GAP letters as the mark; dusty denim line, kept duller than
  // red-bull's #5b8def and prada's #5ac8fa so the blues stay apart
  gap: { bg: "#002a5f", fg: "#fff", mono: "GP", line: "#6c8ebf" },
  // Uniqlo's official red for the box mark (user call 2026-09-06: real brand
  // colours on the tiles); the rose line keeps clear of direction-red
  uniqlo: { bg: "#ff0000", fg: "#fff", mono: "UQ", line: "#ff9aa8" },
  // lululemon's official red disc (the omega is a hole in it, so the mark is
  // painted red on a white tile — the logo as the brand shows it; user call
  // 2026-09-06: real brand colours); deeper magenta line so the series
  // stays apart from uniqlo's rose and direction-red
  lululemon: { bg: "#ffffff", fg: "#d31334", mono: "LL", line: "#e0629b" },
  // ---- tech cohort (2026-08-31) ----
  // Apple's space grey is its own; the series line is NOT silver, because
  // dior, zara and nike already hold the neutral greys — a compare chart needs
  // two lines a viewer can tell apart, so it takes the unoccupied cyan
  apple: { bg: "#1d1d1f", fg: "#f5f5f7", mono: "AP", line: "#2fb8c9" },
  // Netflix red is direction-red, so it stays on the tile (the mcdonalds
  // gold-on-charcoal precedent) and the line steps sideways into magenta
  netflix: { bg: "#141414", fg: "#e50914", mono: "N", line: "#ff5c8a" },
  // Instagram's current brand magenta as the tile (the 2022 hex, user call
  // 2026-09-06: real brand colours), the gradient's purple end as the line
  // (kept darker than accent #8b7bff)
  instagram: { bg: "#ff0069", fg: "#fff", mono: "IG", line: "#b06ab3" },
  // Duo's official green on the tile (user call 2026-09-06: real brand
  // colours, even though it sits near direction-green), white owl on it, the
  // line the owl's feather yellow
  duolingo: { bg: "#58cc02", fg: "#fff", mono: "DL", line: "#f2c14e" },
  // Anthropic's clay on the tile; the line goes browner — the one hue family
  // no brand on the board holds yet
  anthropic: { bg: "#d97757", fg: "#fff", mono: "AN", line: "#c98b6b" },
  // Jacquemus' lavender-field palette (its most-photographed show); the line
  // stays blue-purple so it reads apart from valentino's magenta orchid
  // #cf8be0 and instagram's #b06ab3 (placeholder pending sign-off)
  jacquemus: { bg: "#cbbde3", fg: "#2b2b2b", mono: "JQ", line: "#9d8ec9" },
  // ---- automotive cohort (2026-09-01) ----
  // Tesla's own near-black; its red would be a fourth red tile and reads as
  // direction-red, so identity sits in the ground and the line takes dark steel
  // — deliberately darker than dior's #c5cbd6 and zara's #b0b7bd
  tesla: { bg: "#171a20", fg: "#fff", mono: "T", line: "#7d8fa8" },
  // the shield rather than the paint: Rosso ground with Modena yellow as the
  // mark, which keeps Ferrari off a third crimson tile. The line takes the
  // shield's amber (rank-gold #ffd60a is reserved; hermes' #ff9500 is the
  // nearest neighbour and sits a touch more orange)
  ferrari: { bg: "#d40000", fg: "#ffe000", mono: "F", line: "#ffab00" },
  // ---- beauty cohort (2026-09-02) ----
  // the black store rather than a seventh near-black clash: Sephora's ground is
  // genuinely black, and the line takes Rouge (its own tier name) kept deeper
  // and pinker than change-red #ff5449 and hotter than netflix's #ff5c8a
  sephora: { bg: "#0d0d0d", fg: "#fff", mono: "SE", line: "#e03562" },
  // the amber bottle as the ground — tobacco rather than louis-vuitton's dark
  // brown #40342a or anthropic's clay #d97757 — with the label's sage as the
  // line, an unoccupied hue kept dull enough not to read as change-green #00c805
  // Aesop's cream ground with the near-black wordmark (user call 2026-09-06:
  // real brand colours on the tiles); the sage line is untouched
  aesop: { bg: "#fffef2", fg: "#252525", mono: "AE", line: "#9aa87c" },
  // Rhode's greige minimalism, a step darker than chanel's cream #ece7dc so the
  // two light tiles stay apart; dusty-rose line, pinker than anthropic's #c98b6b
  // and much softer than lululemon's #e0629b (placeholder pending sign-off)
  rhode: { bg: "#ded5c9", fg: "#1a1a1a", mono: "RH", line: "#d3a0a8" },
  // ---- ouai (2026-09-10, joins the beauty board) ----
  // OUAI's white-label minimalism — a white ground with near-black ink (the
  // lululemon tile reading, not another cream/greige beside aesop and rhode);
  // the line takes a muted lilac, an unoccupied hue among brand lines, kept
  // deeper than the beauty INDUSTRY line's orchid #c49ad6 so brand and
  // industry series stay apart on the stats screens (placeholder pending
  // sign-off)
  ouai: { bg: "#ffffff", fg: "#1a1a1a", mono: "OU", line: "#9d7fc2" },
  // Estée Lauder (2026-09-23): the house's navy — its wordmark and the
  // Advanced Night Repair bottle's ground — under a gilt monogram; the line
  // takes the same navy, deeper and greyer than red-bull's #5b8def and
  // automotive's #7fb4e0 so the blues stay apart (placeholder pending
  // sign-off)
  "estee-lauder": { bg: "#0b2a4a", fg: "#e8d9a8", mono: "EL", line: "#3d6aa3" },
  // ---- jewelry cohort (2026-09-02) ----
  // Tiffany Blue is the strongest single brand code on the board, so it takes
  // the tile unaltered. The LINE steps deeper rather than reusing it: teal is
  // the most crowded family here (gucci #4fd8c4, apple #2fb8c9, food's jade
  // #62c99e), and #0f7d78 is darker than all three so a compare chart stays
  // readable
  tiffany: { bg: "#0abab5", fg: "#fff", mono: "TF", line: "#0f7d78" },
  // Pandora's rose gold. Pink is the board's most crowded hue, so the line goes
  // muted and greyed — darker than rhode's #d3a0a8 and bottega-veneta's
  // #e08fa9, and browner than lululemon's #e0629b (placeholder pending sign-off)
  pandora: { bg: "#b76e79", fg: "#fff", mono: "PD", line: "#a97b86" },
  // ---- ugg (2026-09-09, joins the fashion board) ----
  // the chestnut suede of the classic boot; the line goes deeper and browner
  // than anthropic's clay #c98b6b and warmer than pandora's rose-brown #a97b86
  // so the tan family stays readable on a compare chart (placeholder pending
  // sign-off)
  ugg: { bg: "#b5794a", fg: "#fff", mono: "UG", line: "#a05f28" },
  // ---- adidas (2026-10-05, joins the fashion board; styled 2026-10-06 when
  // its PHANTOM row left mock/phantomBrands) ----
  // black-and-white by identity, nike's case exactly, so the tile record is
  // nike's inverse and the LINE a graphite — darker than zara's cool grey
  // #b0b7bd and well under nike's near-white #f2f2f2, so the three
  // monochrome houses stay apart on a compare chart; the Originals blue is
  // NOT used, the blues (red-bull, prada, gap) are the most crowded family
  // after pink (placeholder pending sign-off)
  adidas: { bg: "#ffffff", fg: "#111111", mono: "AD", line: "#8c929a" },
  // ---- dr-martens (2026-10-05, joins the fashion board) ----
  // black leather under the yellow welt stitch — the brand's two codes. The
  // LINE cannot take the stitch yellow (rank-gold) so it takes the 1460's
  // cherry red, pulled down to an oxblood: darker and bluer than
  // direction-red, deeper than pandora's rose-brown #a97b86 (placeholder
  // pending sign-off)
  "dr-martens": { bg: "#111111", fg: "#ffd100", mono: "DM", line: "#a8475a" },
  // ---- off-board brands (Lab openings feed + daily-briefing actors) ----
  // Never ranked, never charted — tiles only, so `line` is nominal. Same law
  // as above: identity says WHO and stays clear of direction-green/red,
  // accent and rank-gold (all placeholders pending sign-off).
  glossier: { bg: "#f6c6d3", fg: "#3a2b30", mono: "GL", line: "#e3a3b6" },
  "gentle-monster": {
    bg: "#232328",
    fg: "#f2f2f2",
    mono: "GM",
    line: "#9aa0ad",
  },
  aritzia: { bg: "#f2ede4", fg: "#221f1a", mono: "AR", line: "#c9bfa8" },
  "alo-yoga": { bg: "#d8cec2", fg: "#26221d", mono: "AL", line: "#b3a48f" },
  arcteryx: { bg: "#252b33", fg: "#f4f5f7", mono: "AX", line: "#7f93a8" },
  "blank-street": { bg: "#bfe0d2", fg: "#173328", mono: "BS", line: "#7fbfa4" },
  // POP MART's white-box red — inverted so it isn't a fifth red GROUND
  "pop-mart": { bg: "#ffffff", fg: "#e0242a", mono: "PM", line: "#e0808a" },
  "dutch-bros": { bg: "#2a63d4", fg: "#fff", mono: "DB", line: "#6f9be6" },
  // Cartier — the Tiffany briefing's opening actor. Its two codes are both
  // spoken for (the box red would read as direction-red, the script gold as
  // rank-gold #ffd60a), so the line takes a muted bronze: browner than
  // aesop-era #e0a458 and duller than #f2c14e
  cartier: { bg: "#ffffff", fg: "#a8112a", mono: "CA", line: "#c08a2e" },
  // Dunkin's orange-on-white lockup; orange sits between hermes #f37021 and
  // anthropic's clay without touching either tile family
  dunkin: { bg: "#ffffff", fg: "#ff671f", mono: "DK", line: "#ff9466" },
  // the UGG briefing's rival actor (2026-09-10): Crocs appears on the board
  // only as another brand's PARTNER (Red Bull Racing, 7-Eleven), so it needs
  // a tile and nothing else — clog green, a shade off duolingo's #58cc02
  crocs: { bg: "#3f9b3a", fg: "#fff", mono: "CR", line: "#7fc47a" },
};
export const BRAND_STYLE = inkTable(BRAND_STYLE_AUTHORED, "line");
const BRAND_FALLBACK_LINE = ink("#8ea0b5");
const BRAND_FALLBACK_LINE_DUSK = duskInk("#8ea0b5");
export const bstyle = (slug) =>
  BRAND_STYLE[slug] || {
    bg: "#2a2f37",
    fg: "#fff",
    mono: (slug || "?").slice(0, 2).toUpperCase(),
    line: BRAND_FALLBACK_LINE,
    lineDusk: BRAND_FALLBACK_LINE_DUSK,
  };

export const CUR_SYM = {
  USD: "$",
  EUR: "€",
  GBP: "£",
  JPY: "¥",
  CHF: "CHF ",
  HKD: "HK$",
};
